<template>
  <div
      class="cws-container cws-sidebar-right blog-wrapper"
  >

    <!-- content -->
    <div class="blog-detail-wrapper">
      <b-row>
        <b-col cols="12">
          <b-card>
            <b-overlay
                :show="isLoading"
                rounded="sm"
            >

              <!-- Track info -->
              <div v-if="trackData">
                <h4>Трек</h4>
                <b-card class="mb-2" border-variant="primary">
                  <h5>{{ trackData.artist }} — {{ trackData.track }}</h5>
                  <div class="d-flex align-items-center">
                    <span class="mr-2">Прослушивания: {{ formatNumber(trackData.play_count) }}</span>
                    <b-button
                        v-if="trackData.file"
                        variant="success"
                        class="btn-icon"
                        size="sm"
                        :href="trackData.url"
                    >
                      <feather-icon icon="SaveIcon"/>
                    </b-button>
                  </div>
                </b-card>
              </div>

              <!-- Original -->
              <div v-if="originalData">
                <h4>Оригинал</h4>
                <b-card class="mb-2" border-variant="success">
                  <div class="d-flex align-items-center justify-content-between">
                    <div>
                      <h5 class="mb-0">{{ originalData.artist }} — {{ originalData.track }}</h5>
                      <span>Прослушивания: {{ formatNumber(originalData.play_count) }}</span>
                    </div>
                    <div>
                      <b-button
                          variant="info"
                          class="btn-icon"
                          :to="{name: 'track-covers', params: { id: originalData.id }}"
                      >
                        <feather-icon icon="DiscIcon"/>
                      </b-button>
                      <b-button
                          variant="danger"
                          class="btn-icon ml-1"
                          @click="removeOriginal"
                      >
                        <feather-icon icon="XIcon"/>
                      </b-button>
                    </div>
                  </div>
                </b-card>
              </div>

              <!-- Set original (when no original and no covers) -->
              <div v-if="trackData && !originalData && !(coversData && coversData.length)">
                <h4>Оригинал</h4>
                <b-card class="mb-2" border-variant="secondary">
                  <div class="text-muted mb-1">Оригинал не задан</div>
                  <div class="d-flex align-items-center">
                    <b-form-input
                        v-model="searchOriginalQuery"
                        placeholder="Поиск трека для оригинала..."
                        class="mr-1"
                        @keyup.enter="searchForOriginal"
                    />
                    <b-button
                        variant="primary"
                        class="text-nowrap"
                        @click="searchForOriginal"
                        :disabled="!searchOriginalQuery.trim()"
                    >
                      <feather-icon icon="SearchIcon" class="mr-50"/>
                      <span class="align-middle">Поиск</span>
                    </b-button>
                  </div>
                  <div v-if="searchOriginalResults.length" class="mt-1">
                    <h6>Результаты:</h6>
                    <div
                        v-for="result in searchOriginalResults"
                        :key="result.id"
                        class="d-flex align-items-center justify-content-between mt-1"
                    >
                      <span>{{ result.artist }} — {{ result.track }}
                        <small class="text-muted">[{{ formatNumber(result.play_count) }}]</small>
                      </span>
                      <b-button
                          variant="success"
                          class="btn-icon ml-1"
                          size="sm"
                          @click="setOriginal(result.id)"
                      >
                        <feather-icon icon="CheckIcon"/>
                      </b-button>
                    </div>
                  </div>
                </b-card>
              </div>

              <!-- Covers table -->
              <div v-if="coversData && !originalData">
                <h4>Каверы [{{ coversData.length }}]</h4>
                <b-table
                    :items="coversData"
                    responsive
                    :fields="tableColumns"
                    primary-key="id"
                    show-empty
                    empty-text="Нет каверов"
                    class="position-relative"
                >

                  <template #cell(play_count)="data">
                    <span class="text-nowrap">
                      {{ formatNumber(data.item.play_count) }}
                    </span>
                  </template>

                  <template #cell(actions)="data">
                    <span class="text-nowrap">
                      <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="info"
                          class="btn-icon"
                          :to="{name: 'track-covers', params: { id: data.item.id }}"
                      >
                        <feather-icon icon="DiscIcon"/>
                      </b-button>
                      <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="warning"
                          class="btn-icon ml-1"
                          :to="{name: 'track-search', params: { id: data.item.id }}"
                      >
                        <feather-icon icon="DownloadIcon"/>
                      </b-button>
                      <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="danger"
                          class="btn-icon ml-1"
                          @click="removeCover(data.item.id)"
                      >
                        <feather-icon icon="XIcon"/>
                      </b-button>
                    </span>
                  </template>
                </b-table>
              </div>

              <!-- Add cover -->
              <div v-if="trackData && !originalData" class="mt-2">
                <h4>Добавить кавер</h4>
                <div class="d-flex align-items-center">
                  <b-form-input
                      v-model="searchCoverQuery"
                      placeholder="Поиск трека для кавера..."
                      class="mr-1"
                      @keyup.enter="searchForCover"
                  />
                  <b-button
                      variant="primary"
                      class="text-nowrap"
                      @click="searchForCover"
                      :disabled="!searchCoverQuery.trim()"
                  >
                    <feather-icon icon="SearchIcon" class="mr-50"/>
                    <span class="align-middle">Поиск</span>
                  </b-button>
                </div>
                <div v-if="searchCoverResults.length" class="mt-1">
                  <h6>Результаты:</h6>
                  <div
                      v-for="result in searchCoverResults"
                      :key="result.id"
                      class="d-flex align-items-center justify-content-between mt-1"
                  >
                    <span>{{ result.artist }} — {{ result.track }}
                      <small class="text-muted">[{{ formatNumber(result.play_count) }}]</small>
                    </span>
                    <b-button
                        variant="success"
                        class="btn-icon ml-1"
                        size="sm"
                        @click="addCover(result.id)"
                    >
                      <feather-icon icon="PlusIcon"/>
                    </b-button>
                  </div>
                </div>
              </div>

              <div v-if="!trackData && !isLoading" class="text-center text-muted py-2">
                Данные не найдены
              </div>

            </b-overlay>
          </b-card>
        </b-col>
      </b-row>
    </div>
    <!--/ content -->

  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BOverlay,
  BTable,
  BFormInput,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import {formatNumber} from '@core/utils/filter'
import router from "@/router";
import store from "@/store";
import musicStoreModule from "@/views/db-music/musicStoreModule";
import {onUnmounted, ref} from "@vue/composition-api";
import {useToast} from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BOverlay,
    BTable,
    BFormInput,
  },
  directives: {
    Ripple,
  },
  setup() {
    const STORE_MODULE_NAME = 'artists'
    const toast = useToast()

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const trackData = ref(null)
    const originalData = ref(null)
    const coversData = ref(null)
    const isLoading = ref(false)

    const searchOriginalQuery = ref('')
    const searchOriginalResults = ref([])
    const searchCoverQuery = ref('')
    const searchCoverResults = ref([])

    const tableColumns = [
      {key: 'id', label: '#', sortable: false},
      {key: 'artist', sortable: true},
      {key: 'track', sortable: true},
      {key: 'play_count', sortable: true},
      {key: 'actions'},
    ]

    const trackId = () => router.currentRoute.params.id

    const fetchCovers = async () => {
      isLoading.value = true
      try {
        const res = await store.dispatch('artists/fetchTrackCovers', {id: trackId()})
        trackData.value = res.data.track
        originalData.value = res.data.original
        coversData.value = res.data.covers
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки каверов',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const searchForOriginal = async () => {
      const q = searchOriginalQuery.value.trim()
      if (!q) return
      isLoading.value = true
      try {
        const excludeArtistId = trackData.value ? trackData.value.artist_id : null
        const res = await store.dispatch('artists/searchTracksInDb', {q, exclude_id: excludeArtistId})
        searchOriginalResults.value = res.data.data
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка поиска треков',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const setOriginal = async (originalId) => {
      isLoading.value = true
      try {
        await store.dispatch('artists/setTrackOriginal', {id: trackId(), original_id: originalId})
        searchOriginalQuery.value = ''
        searchOriginalResults.value = []
        await fetchCovers()
        toast({
          component: ToastificationContent,
          props: {
            title: 'Оригинал установлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка установки оригинала',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const removeOriginal = async () => {
      isLoading.value = true
      try {
        await store.dispatch('artists/removeTrackOriginal', {id: trackId()})
        await fetchCovers()
        toast({
          component: ToastificationContent,
          props: {
            title: 'Оригинал удалён',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка удаления оригинала',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const searchForCover = async () => {
      const q = searchCoverQuery.value.trim()
      if (!q) return
      isLoading.value = true
      try {
        const excludeArtistId = trackData.value ? trackData.value.artist_id : null
        const res = await store.dispatch('artists/searchTracksInDb', {q, exclude_id: excludeArtistId})
        searchCoverResults.value = res.data.data
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка поиска треков',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const addCover = async (coverId) => {
      isLoading.value = true
      try {
        await store.dispatch('artists/addTrackCover', {id: trackId(), cover_id: coverId})
        searchCoverResults.value = searchCoverResults.value.filter(t => t.id !== coverId)
        await fetchCovers()
        toast({
          component: ToastificationContent,
          props: {
            title: 'Кавер добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка добавления кавера',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const removeCover = async (coverId) => {
      isLoading.value = true
      try {
        await store.dispatch('artists/removeTrackCover', {id: trackId(), cover_id: coverId})
        await fetchCovers()
        toast({
          component: ToastificationContent,
          props: {
            title: 'Кавер удалён',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка удаления кавера',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    fetchCovers()

    return {
      trackData,
      originalData,
      coversData,
      isLoading,
      tableColumns,
      formatNumber,
      searchOriginalQuery,
      searchOriginalResults,
      searchForOriginal,
      setOriginal,
      removeOriginal,
      searchCoverQuery,
      searchCoverResults,
      searchForCover,
      addCover,
      removeCover,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
</style>
